import { ControlBar, ErrorBanner } from '@/components';
import { SenderEmailType, SenderInfoAction } from '@/constants';
import { addMinuteQuantity, capitalizeFirstLetter } from '@/helpers';
import { getErrorFromAPI, handleToastMutation } from '@/helpers/toast';
import {
  useConnectSesMutation,
  useDisconnectEmailMutation,
  useGetNotificationAccountsQuery,
  useResendSesEmailMutation,
  useUpdateEmailAccountMutation,
} from '@/redux/api/api.caller';
import { tokenSelector } from '@/redux/features/auth.slice';
import commonSlice from '@/redux/features/common.slice';
import emailSlice, { resendTimeEndSelector, senderInfoSelector, showVerifyEmailSelector } from '@/redux/features/email.slice';
import { IEmailAccountPayload, ISesConnectPayload } from '@/types/notification';
import { Badge, BlockStack, Button, Card, Icon, Modal, RadioButton, TextField } from '@shopify/polaris';
import { InfoIcon } from '@shopify/polaris-icons';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ResendMailCountDown from '../ResendMailCountDown';
import VerifiedEmailPopup from '../VerifiedEmailPopup';
import { StyledSenderInfo } from './styled';

const SenderInfo = () => {
  const [apiError, setApiError] = useState<{ connect: string; update: string; disconnect: string }>({
    connect: '',
    update: '',
    disconnect: '',
  });
  const dispatch = useDispatch();
  const globalState = useSelector(senderInfoSelector);
  const haveShownVerifyEmail = useSelector(showVerifyEmailSelector);
  const { shop } = useSelector(tokenSelector);
  const { data } = useGetNotificationAccountsQuery();
  const accountData = data?.data?.email?.account;
  const [createAccount, connectSesData] = useConnectSesMutation();
  const [disconnectAccount, disconnectEmailData] = useDisconnectEmailMutation();
  const [updateAccount, updateAccountData] = useUpdateEmailAccountMutation();
  const [resendSesEmail, { isLoading: isResendEmailFetching }] = useResendSesEmailMutation();
  const resendTimeEnd = useSelector(resendTimeEndSelector);
  const [subtractCountDownTime, setSubtractCountDownTime] = useState<number>(0);

  const [loaded, setLoaded] = useState<{ email: string; name: string; disable: boolean }>({ email: '', name: '', disable: false });

  const isDirty =
    globalState.type === SenderEmailType.YourEmail &&
    (globalState.account.value !== loaded.email || globalState.name !== loaded.name);

  const handleDiscardSender = () => {
    setApiError({ connect: '', update: '', disconnect: '' });
    dispatch(
      emailSlice.actions.handleChangeSenderInfo({
        ...globalState,
        account: { ...globalState.account, value: loaded.email, error: '', disable: loaded.disable },
        name: loaded.name,
      }),
    );
  };

  const handleSaveSender = () =>
    handleSubmit(
      !accountData || (accountData && accountData.useDefaultEmail) || (accountData && !accountData.email)
        ? SenderInfoAction.Connect
        : SenderInfoAction.Edit,
    );

  const handleCloseNoReplyModal = () => {
    setApiError({
      ...apiError,
      disconnect: '',
    });
    dispatch(emailSlice.actions.handleChangeSenderInfo({ ...globalState, isNoReplyModalActive: false }));
  };
  const handleCloseDisconnectedModal = () => {
    setApiError({
      ...apiError,
      disconnect: '',
    });
    dispatch(emailSlice.actions.handleChangeSenderInfo({ ...globalState, isDisconnectedModalActive: false }));
  };
  const handleCloseVerifiedEmailPopup = () => {
    dispatch(emailSlice.actions.handleChangeSenderInfo({ ...globalState, isNotVerifiedPopupActive: false }));
    dispatch(emailSlice.actions.handleShowVerifyEmail(true));
  };

  const handleAfterDisconnect = (data: any) => {
    dispatch(commonSlice.actions.handleToast(handleToastMutation(data)));
    dispatch(emailSlice.actions.handleShowVerifyEmail(false));
    dispatch(
      emailSlice.actions.handleChangeSenderInfo({
        ...globalState,
        type: SenderEmailType.NoReply,
        account: { value: '', error: '', disable: false },
        isNotVerifiedPopupActive: false,
        isNoReplyModalActive: false,
        isDisconnectedModalActive: false,
      }),
    );
  };

  const handleChange = (value: string | boolean, key: string) => {
    setApiError({ connect: '', update: '', disconnect: '' });
    if (key === SenderEmailType.NoReply || key === SenderEmailType.YourEmail) {
      if (key === SenderEmailType.NoReply && accountData && !accountData.useDefaultEmail) {
        return dispatch(emailSlice.actions.handleChangeSenderInfo({ ...globalState, isNoReplyModalActive: true }));
      }
      return dispatch(emailSlice.actions.handleChangeSenderInfo({ ...globalState, type: key as SenderEmailType }));
    }
    dispatch(
      emailSlice.actions.handleChangeSenderInfo({
        ...globalState,
        [key]: key === 'account' ? { ...globalState.account, value, error: !value && 'Field is required' } : value,
      }),
    );
  };

  const handleEditOrDisconnectEmail = (type: SenderInfoAction) => {
    if (type === SenderInfoAction.Disconnect) {
      return dispatch(emailSlice.actions.handleChangeSenderInfo({ ...globalState, isDisconnectedModalActive: true }));
    }
    dispatch(
      emailSlice.actions.handleChangeSenderInfo({
        ...globalState,
        account: {
          ...globalState.account,
          disable: false,
          value: globalState.account.value,
        },
      }),
    );
  };

  const handleConfirmDisconnectEmail = () =>
    disconnectAccount({ useDefaultEmail: false }).then((data) => {
      if ('data' in data && data.data && data.data?.state === 1) {
        setApiError({
          ...apiError,
          disconnect: '',
        });
        handleAfterDisconnect(data);
        return;
      }
      setApiError({
        ...apiError,
        disconnect: getErrorFromAPI(data),
      });
    });

  const handleChangeToNoReplyEmail = () => {
    disconnectAccount({ useDefaultEmail: true }).then((data) => {
      if ('data' in data && data.data && data.data?.state === 1) {
        setApiError({
          ...apiError,
          disconnect: '',
        });
        handleAfterDisconnect(data);
        return;
      }
      setApiError({
        ...apiError,
        disconnect: getErrorFromAPI(data),
      });
    });
  };

  const handleSubmit = (type: SenderInfoAction) => {
    if (type === SenderInfoAction.Connect) {
      const connectedPayload: ISesConnectPayload = {
        email: globalState.account.value,
        senderName: globalState.name,
      };
      return createAccount(connectedPayload).then((data) => {
        if ('data' in data && data.data && data.data?.state === 1) {
          setApiError({
            ...apiError,
            connect: '',
          });
          dispatch(commonSlice.actions.handleToast(handleToastMutation(data)));
        }
        if ('error' in data) {
          setApiError({
            ...apiError,
            connect: getErrorFromAPI(data),
          });
        }
      });
    }
    const updatedPayload: IEmailAccountPayload = {
      email: globalState.account.value,
      senderName: globalState.name,
    };
    updateAccount(updatedPayload).then((data) => {
      if ('data' in data && data.data && data.data?.state === 1) {
        setApiError({
          ...apiError,
          update: '',
        });
        dispatch(commonSlice.actions.handleToast(handleToastMutation(data)));
        dispatch(
          emailSlice.actions.handleChangeSenderInfo({
            ...globalState,
            account: { ...globalState.account, disable: !!data.data?.state },
          }),
        );
        return;
      }
      setApiError({
        ...apiError,
        update: getErrorFromAPI(data),
      });
    });
  };

  const handleResendVerifyEmail = () => {
    resendSesEmail({ email: globalState.account.value }).then((data) => {
      if ('data' in data && data.data && data.data?.state === 1) {
        setApiError({
          ...apiError,
          connect: '',
        });
        dispatch(emailSlice.actions.handleSetResendTimeEnd(addMinuteQuantity(1)));
        dispatch(commonSlice.actions.handleToast(handleToastMutation(data)));
      }
      if ('error' in data) {
        setApiError({
          ...apiError,
          connect: getErrorFromAPI(data),
        });
      }
    });
  };

  useEffect(() => {
    if (accountData) {
      const email = accountData.useDefaultEmail ? '' : accountData.email || '';
      const disable = !accountData.useDefaultEmail && !!accountData.email;
      setLoaded({ email, name: accountData.senderName || '', disable });
      dispatch(
        emailSlice.actions.handleChangeSenderInfo({
          ...globalState,
          account: {
            ...globalState.account,
            value: email,
            disable,
          },
          name: accountData.senderName || '',
          type: accountData.useDefaultEmail ? SenderEmailType.NoReply : SenderEmailType.YourEmail,
          isNotVerifiedPopupActive: !accountData.useDefaultEmail && !accountData.isVerified && !!accountData.email,
        }),
      );
      return;
    }
    setLoaded({ email: '', name: shop.split('.')[0], disable: false });
    dispatch(emailSlice.actions.handleChangeSenderInfo({ ...globalState, name: shop.split('.')[0] }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountData]);

  useLayoutEffect(() => {
    let interval: any;
    if (resendTimeEnd) {
      interval = setInterval(() => {
        setSubtractCountDownTime(new Date(resendTimeEnd).getTime() - new Date(new Date().getTime() - 1 * 1000).getTime());
      }, 1000);
    } else {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [resendTimeEnd]);

  return (
    <StyledSenderInfo>
      <ControlBar
        isFixed
        hidden={!isDirty}
        actionGroup={[
          { content: 'Discard', onAction: handleDiscardSender, primary: false },
          { content: 'Save', onAction: handleSaveSender, primary: true, disabled: !globalState.account.value, loading: connectSesData.isLoading || updateAccountData.isLoading },
        ]}
      />
      <Card>
        <BlockStack gap="400">
          <ErrorBanner isVisible={!!apiError.connect || !!apiError.update}>{apiError.connect || apiError.update}</ErrorBanner>
          <div className="sender-email">
            <div className="content">
              <RadioButton
                id={SenderEmailType.NoReply}
                name="senderEmail"
                label="no-reply@trakow.com"
                helpText={
                  <>
                    We will be using the email address {<span className="no-reply">no-reply@trakow.com</span>} to send
                    messages to your customers.
                  </>
                }
                checked={globalState.type === SenderEmailType.NoReply}
                onChange={handleChange}
              />
              <RadioButton
                id={SenderEmailType.YourEmail}
                name="senderEmail"
                label="Use your email as sender’s"
                helpText="Connect your own business mail which you use to send messages to your customers"
                checked={globalState.type === SenderEmailType.YourEmail}
                onChange={handleChange}
              />
              {globalState.type === SenderEmailType.YourEmail && (
                <div className="email-as-sender">
                  <div className="your-email">
                    <div className="email-title">
                      Your email
                      {accountData &&
                        globalState.account.disable &&
                        !globalState.account.error &&
                        !accountData.useDefaultEmail && (
                          <Badge
                            progress={accountData.isVerified ? 'complete' : 'partiallyComplete'}
                            tone={accountData.isVerified ? 'success' : 'attention'}
                          >
                            {accountData.isVerified ? 'verified' : 'not verified'}
                          </Badge>
                        )}
                    </div>
                    <TextField
                      id="account"
                      label="Your email"
                      labelHidden
                      value={globalState.account.value}
                      onChange={handleChange}
                      autoComplete="off"
                      disabled={globalState.account.disable}
                      connectedRight={
                        accountData &&
                        accountData.email &&
                        !accountData.useDefaultEmail && (
                          <Button
                            onClick={() =>
                              handleEditOrDisconnectEmail(
                                accountData.isVerified ? SenderInfoAction.Disconnect : SenderInfoAction.Edit,
                              )
                            }
                          >
                            {accountData.isVerified
                              ? capitalizeFirstLetter(SenderInfoAction.Disconnect)
                              : capitalizeFirstLetter(SenderInfoAction.Edit)}
                          </Button>
                        )
                      }
                      error={globalState.account.error}
                      helpText={
                        accountData &&
                        !accountData.isVerified &&
                        globalState.account.disable &&
                        !globalState.account.error && (
                          <>
                            <div className="not-verified">
                              <Icon source={InfoIcon} tone="base" />
                              <span>Please verify this email via a link provided by checking your mail box</span>
                            </div>
                            <div className="resend-container">
                              Didn&apos;t you receive the verification email?{' '}
                              {subtractCountDownTime > 0 ? (
                                <ResendMailCountDown subtractTime={subtractCountDownTime} />
                              ) : (
                                <Button variant="plain" disabled={isResendEmailFetching} onClick={handleResendVerifyEmail}>
                                  Resend it
                                </Button>
                              )}
                            </div>
                          </>
                        )
                      }
                    />
                  </div>
                  <TextField
                    id="name"
                    label="Sender name"
                    value={globalState.name}
                    onChange={handleChange}
                    autoComplete="off"
                    maxLength={30}
                    showCharacterCount
                  />
                </div>
              )}
            </div>
          </div>
        </BlockStack>
      </Card>

      <Modal
        open={globalState.isNoReplyModalActive}
        onClose={handleCloseNoReplyModal}
        title={
          <>
            Changing Email to <span style={{ textDecoration: 'underline' }}>no-reply@trakow.com</span>
          </>
        }
        primaryAction={{
          content: 'Yes, I do',
          onAction: handleChangeToNoReplyEmail,
        }}
        secondaryActions={[
          {
            content: 'No',
            onAction: handleCloseNoReplyModal,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <ErrorBanner isVisible={!!apiError.disconnect}>{apiError.disconnect}</ErrorBanner>
            Changing your email address to <span style={{ textDecoration: 'underline' }}>no-reply@trakow.com</span> will also
            result in disconnecting your account from the app. Are you sure you want to proceed with this change?
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={globalState.isDisconnectedModalActive}
        onClose={handleCloseDisconnectedModal}
        title="Disconnect email"
        primaryAction={{
          content: 'Disconnect',
          destructive: true,
          onAction: handleConfirmDisconnectEmail,
          loading: disconnectEmailData.isLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleCloseDisconnectedModal,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <ErrorBanner isVisible={!!apiError.disconnect}>{apiError.disconnect}</ErrorBanner>
            Are you sure you want to disconnect this email account?
          </BlockStack>
        </Modal.Section>
      </Modal>

      <VerifiedEmailPopup
        email={globalState.account.value}
        isOpen={globalState.isNotVerifiedPopupActive && !haveShownVerifyEmail}
        onClosePopup={handleCloseVerifiedEmailPopup}
      />
    </StyledSenderInfo>
  );
};

export default SenderInfo;
